import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { PayorContext } from "@app/provider";
import { Payor } from "@app/provider/payor";
import { TypeAhead, TypeAheadOption } from "@natera/form";
import { ErrorController } from "@natera/platform/lib/hooks";

import { usePayorsTypeAheadAria } from "./payorsTypeAheadAria";
import {
  getPayorOptionId,
  getPayorsListboxId,
  payorFieldId,
} from "./payorsTypeAheadIds";

import "./payorsTypeAhead.scss";

export { getPayorOptionId, payorFieldId } from "./payorsTypeAheadIds";

const messages = defineMessages({
  payorsTypeAheadPlaceholder: {
    id: "payorsTypeAheadPlaceholder",
    defaultMessage: "Enter insurance company",
  },
  payorsTypeAheadScreenReaderText: {
    id: "payorsTypeAheadScreenReaderText",
    defaultMessage:
      "Begin typing to search, use arrow keys to navigate, Enter to select",
  },
  payorsTypeAheadListDescription: {
    id: "payorsTypeAheadListDescription",
    defaultMessage: "Insurance companies",
  },
});

const SEARCHING_DELAY_TIME = 600;
const PAYORS_LIMIT = 100;

export interface PayorsTypeAheadProps {
  selectedPayor?: Payor;
  onSelect: (item?: Payor) => void;
  errorController?: ErrorController;
  labelId?: string;
}

const PayorsTypeAhead: React.FC<PayorsTypeAheadProps> = ({
  selectedPayor,
  onSelect,
  errorController,
  labelId,
}) => {
  const intl = useIntl();
  const { getPayors } = React.useContext(PayorContext);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const optionsRef = React.useRef<Payor[]>([]);
  const [isFocused, setIsFocused] = React.useState(false);
  const listboxId = getPayorsListboxId();

  const { isExpanded, notifyOptionsChanged } = usePayorsTypeAheadAria({
    containerRef,
    selectedPayor,
    labelId,
    listboxId,
    optionsRef,
  });

  const handlePayorSearch = React.useCallback(
    async (value: string) => {
      const results = await getPayors(value.trim(), PAYORS_LIMIT);
      optionsRef.current = results;
      notifyOptionsChanged();
      return results;
    },
    [getPayors, notifyOptionsChanged]
  );

  const handleSelect = (item?: Payor) => {
    errorController?.clearValidationError(payorFieldId);
    onSelect(item);
  };

  const displayPayorValue = (option: Payor) => option.displayName;

  return (
    <div ref={containerRef} className="payors-type-ahead-container">
      <div
        id="instructions"
        className="visually-hidden"
        aria-hidden={!isFocused}
        aria-live="polite"
      >
        {intl.formatMessage(messages.payorsTypeAheadScreenReaderText)}
      </div>
      <div
        id="list-description"
        className="visually-hidden"
        aria-hidden={!isFocused}
        aria-live="polite"
      >
        {intl.formatMessage(messages.payorsTypeAheadListDescription)}
      </div>
      <TypeAhead
        id={payorFieldId}
        name={payorFieldId}
        className="payors-type-ahead"
        getOptions={handlePayorSearch}
        placeholder={intl.formatMessage(messages.payorsTypeAheadPlaceholder)}
        onSelect={handleSelect}
        selectedOption={selectedPayor}
        displayValue={displayPayorValue}
        searchEmptyValue
        delayTime={SEARCHING_DELAY_TIME}
        pageLimit={PAYORS_LIMIT}
        outline
        floating={true}
        menuClassName="payors-type-ahead__menu"
        role="combobox"
        aria-describedby="instructions"
        aria-labelledby={labelId}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-expanded={isExpanded}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        {(payorOptions: Payor[]) => {
          optionsRef.current = payorOptions;

          return payorOptions.map((option, index) => (
            <TypeAheadOption
              optionValue={option}
              key={`${option.displayName}-${index}`}
              id={getPayorOptionId(index)}
              aria-selected={
                !!selectedPayor &&
                option.displayName.toLowerCase() ===
                  selectedPayor.displayName.toLowerCase()
              }
              aria-roledescription="insurance option"
            >
              {option.displayName}
            </TypeAheadOption>
          ));
        }}
      </TypeAhead>
    </div>
  );
};

export default PayorsTypeAhead;
